import { View } from '@/components/ui/tw-wrappers';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { MultiCountrySelect } from '@/components/multi-country-select';
import { Text } from '@/components/text';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Chip } from '@/components/ui/chip';
import { NumberInput } from '@/components/ui/number-input';
import { Select } from '@/components/ui/select';
import { SCENARIO_TYPES } from '@/constants/scenarios';
import { Country } from '@/types/country';
import { IconSymbol } from './ui/icon-symbol';

import { Colors } from '@/constants/theme';
import { useCountryName } from '@/hooks/useCountryName';
import { useBenchmarkingStore } from '@/stores/benchmarking-store';
import { CommonFormValues, getCommonSchema } from '@/types/common.schema';
import { useFormatters } from '@/utils/formatters/useFormatters';

export function CommonSectionSummary({
  countries,
  countriesLoading,
}: {
  countries: Country[];
  countriesLoading?: boolean;
}) {
  const { t } = useTranslation();

  const SCENARIO_OPTIONS = useMemo(() => {
    return SCENARIO_TYPES.filter(type => type !== 'alsoThere').map(type => ({
      id: type,
      label: t(`scenarios.types.${type}.title`),
    }));
  }, [t]);

  const commonProps = useBenchmarkingStore(s => s.commonProps);
  const setCommonProps = useBenchmarkingStore(s => s.setCommonProps);
  const items = useBenchmarkingStore(s => s.items);
  const setAllBenchmarking = useBenchmarkingStore(s => s.setAllBenchmarking);
  const resetCommonProps = useBenchmarkingStore(s => s.resetCommonProps);
  const { formatNumber } = useFormatters();
  const getLocalizedName = useCountryName();

  const schema = useMemo(() => getCommonSchema(t), [t]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
    reset,
  } = useForm<CommonFormValues>({
    resolver: zodResolver(schema),
    defaultValues: commonProps as any,
    mode: 'onTouched',
  });

  const scenario = watch('scenario');

  const [isEditing, setIsEditing] = useState(false);
  // For display in summary mode
  const vendorList = countries.filter(c => commonProps.vendorIdList.includes(c.id));

  const onEditStart = () => {
    reset(commonProps as any);
    setIsEditing(true);
  };

  const onSave = handleSubmit(values => {
    const updatedItems = items.map(item => {
      // const isCustomerIdInvalid = item.customerId && !values.vendorIdList.includes(item.customerId);
      // const isVendorIdInvalid = item.vendorId && values.vendorIdList.includes(item.vendorId);

      const isCustomerIdInvalid = item.customerId && values.vendorIdList.includes(item.customerId);
      const isVendorIdInvalid = item.vendorId && !values.vendorIdList.includes(item.vendorId);

      if (isCustomerIdInvalid || isVendorIdInvalid) {
        return {
          ...item,
          customerId: isCustomerIdInvalid ? undefined : item.customerId,
          vendorId: isVendorIdInvalid ? undefined : item.vendorId,
        };
      }
      return item;
    });

    setAllBenchmarking(updatedItems);
    setCommonProps(values);
    setIsEditing(false);
  });

  const onCancel = () => {
    setIsEditing(false);
  };

  const onClear = () => {
    resetCommonProps();
    useBenchmarkingStore.getState().resetBenchmarking();
    if (isEditing) {
      reset(commonProps);
    }
  };

  return (
    <Animated.View entering={FadeIn.delay(50)} exiting={FadeOut} style={styles.container}>
      <Card backgroundColor='white'>
        <View className='flex-col items-stretch gap-3 mb-4 pb-5 border-b border-black/6 z-10 md:flex-row md:justify-between md:items-center md:gap-4'>
          <View className='flex-row items-center justify-between md:gap-3 min-h-[39px] flex-1'>
            <Text type='subtitle' style={styles.titleText}>
              {t('onboarding.summary.title', 'Analysis Parameters')}
            </Text>
            {!isEditing && (
              <View style={styles.actions}>
                <Pressable onPress={onClear} hitSlop={20} style={styles.iconPressable}>
                  <IconSymbol name='arrow.clockwise' color='gray' />
                  <Text style={styles.iconLabel}>{t('common.reset')}</Text>
                </Pressable>
                <Pressable onPress={onEditStart} style={styles.iconPressable}>
                  <IconSymbol name='pencil' color='gray' />
                  <Text style={styles.iconLabel}>{t('common.edit')}</Text>
                </Pressable>
              </View>
            )}
          </View>
        </View>

        {/* grid */}
        <View style={styles.grid}>
          {/* 1. Customer list */}
          <View style={styles.item}>
            <Text style={[styles.label, isEditing && { color: Colors.light.primary, opacity: 1 }]}>
              {t('onboarding.summary.vendorCountryList')}
            </Text>
            {!isEditing ? (
              <View style={styles.chipsContainer}>
                {vendorList.map(c => (
                  <Chip key={c.id} label={getLocalizedName(c)} />
                ))}
              </View>
            ) : (
              <View style={styles.editWrap}>
                <Controller
                  control={control}
                  name='vendorIdList'
                  render={({ field: { value, onChange } }) => (
                    <MultiCountrySelect
                      label={''}
                      countries={countries}
                      valueIds={value || []}
                      disabled={countriesLoading}
                      // disabledIds={blockedIds}
                      onChange={ids => {
                        onChange(ids);
                        if (ids.length > 0) clearErrors('vendorIdList');
                      }}
                    />
                  )}
                />
                {errors.vendorIdList && <Text style={styles.errorText}>{errors.vendorIdList.message}</Text>}
              </View>
            )}
          </View>
          {/* 2. Amount */}
          <View style={styles.longItem}>
            <Text style={[styles.label, isEditing && { color: Colors.light.primary, opacity: 1 }]}>
              {t('onboarding.summary.invoicingAmount')}
            </Text>
            {!isEditing ? (
              <Text type='defaultSemiBold' style={styles.value}>
                {formatNumber(commonProps?.invoicingAmount ?? 0)} USD
              </Text>
            ) : (
              <View style={styles.editWrap}>
                <View style={styles.controllerWrap}>
                  <Controller
                    control={control}
                    name='invoicingAmount'
                    render={({ field: { value, onChange } }) => (
                      <NumberInput value={value} placeholder='10000' onChange={onChange} prefix='$' />
                    )}
                  />
                </View>
                {errors.invoicingAmount && <Text style={styles.errorText}>{errors.invoicingAmount.message}</Text>}
              </View>
            )}
          </View>

          {/* 4. Expansion scenario */}
          <View style={styles.item}>
            <Text style={[styles.label, isEditing && { color: Colors.light.primary, opacity: 1 }]}>
              {t('onboarding.fields.scenario')}
            </Text>
            {!isEditing ? (
              <Text type='defaultSemiBold' style={styles.value}>
                {t(`scenarios.types.${commonProps.scenario}.title`)}
              </Text>
            ) : (
              <View style={styles.controllerWrap}>
                <Controller
                  control={control}
                  name='scenario'
                  render={({ field: { value, onChange } }) => (
                    <Select
                      id='scenario'
                      name='scenario'
                      value={value}
                      onValueChange={onChange}
                      options={SCENARIO_OPTIONS.map(s => ({
                        value: s.id,
                        label: s.label,
                      }))}
                      placeholder={t('common.select', 'Select...')}
                    />
                  )}
                />
                {errors.scenario ? <Text style={styles.errorText}>{errors.scenario.message}</Text> : null}
              </View>
            )}
          </View>

          {/* 5. partnerMargin */}
          {scenario === 'localPartner' && (
            <View style={styles.item}>
              <Text style={[styles.label, isEditing && { color: Colors.light.primary, opacity: 1 }]}>
                {t('onboarding.summary.localPartnerMargin', 'Local Partner')}
              </Text>
              {!isEditing ? (
                <Text type='defaultSemiBold' style={styles.value}>
                  {commonProps.partnerMargin}%
                </Text>
              ) : (
                <View style={styles.controllerWrap}>
                  <Controller
                    control={control}
                    name='partnerMargin'
                    render={({ field: { value, onChange } }) => (
                      <NumberInput
                        label={''}
                        value={value}
                        placeholder='%'
                        suffix='%'
                        min={0}
                        max={100}
                        onChange={onChange}
                      />
                    )}
                  />
                  {errors.partnerMargin && <Text style={styles.errorText}>{errors.partnerMargin.message}</Text>}
                </View>
              )}
            </View>
          )}
        </View>
        <View style={styles.footer}>
          {isEditing && (
            <View style={styles.editActions}>
              <Pressable onPress={onCancel} style={styles.cancelPressable}>
                <Text style={{ color: Colors.light.secondaryText }}>{t('common.cancel', 'Cancel')}</Text>
              </Pressable>
              <Button label={t('common.save', 'Save changes')} onPress={onSave} variant='primary' />
            </View>
          )}
        </View>
      </Card>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    alignSelf: 'center',
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  editActions: {
    flexDirection: 'row',
    gap: 38,
    alignItems: 'center',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    alignContent: 'flex-start',
    position: 'relative',
    zIndex: 1,
  },
  item: {
    flexBasis: 280,
    minWidth: 240,
    maxWidth: 320,
    gap: 4,
    position: 'relative',
  },
  longItem: {
    flexBasis: 320,
    minWidth: 240,
    maxWidth: 340,
    gap: 4,
    position: 'relative',
  },
  editWrap: {
    flex: 1,
  },
  controllerWrap: {
    flex: 1,
    gap: 8,
    marginTop: 8,
  },
  label: {
    fontSize: 16,
    opacity: 0.5,
    color: '#666',
    marginBottom: 4,
    height: 24,
  },
  value: {
    fontSize: 14,
    minHeight: 30,
    textAlignVertical: 'center',
  },
  titleText: {
    flexShrink: 1,
  },
  iconPressable: {
    padding: 5,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  iconLabel: {
    color: Colors.light.secondaryText,
    fontSize: 11,
  },
  cancelPressable: {
    padding: 8,
  },
  errorText: {
    color: '#CB2F39',
    fontSize: 11,
    marginTop: 2,
  },
  chipsContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    alignItems: 'center',
    marginTop: 16,
  },
});
